import type {Operation} from '@apollo/client';

import {getEnvironment} from '@sail/utils';
import {MAX_INCLUDE_ONLY} from '@stripe-internal/babel-plugin-data-graphql-helpers';
import {runtimePathBuilder} from './builders';
import type {TopLevelFieldToArgs} from './removePbeFromParams';

import queriesToExecuteUsingPost from './queriesToExecuteUsingPost';
import type {RestLinkOperationContext} from './types';
import {parseQueryParams} from './parseQueryParams';
import removePbeFromParams from './removePbeFromParams';
import rewriteInteropParams from './rewriteInteropParams';
import type {InteropFields} from './rewriteInteropParams';

export default function buildRestPath(
  params: any,
  operation: Operation,
  pbeMap: Record<string, ReadonlyArray<string>>,
  topLevelFieldToArgs: TopLevelFieldToArgs,
  queryEndpointsToBeRequestedWithPost?: string[],
  shadowComparisonOperationsV1ToV2?: Record<string, boolean>,
  interopFields?: InteropFields,
) {
  const {headers, observabilityFns, queryProject}: RestLinkOperationContext =
    operation.getContext();

  removePbeFromParams(
    operation,
    params,
    pbeMap,
    topLevelFieldToArgs,
    observabilityFns,
  );

  rewriteInteropParams(operation, params, interopFields, observabilityFns);

  if (queriesToExecuteUsingPost.has(params['@rest'].runtimePath)) {
    params['@rest'].method = 'POST';
  }

  const {mainDefinition, fragmentDefinitions} = params.context;

  /**
   * If the apiKey V1ContextInput property is specified, we should check
   * whether the API key is a publishable key. If it is, providing the
   * `include_only` request param will cause the API call to fail as it
   * works for dashboard_only API keys only.
   */
  const isDashboardOnlyAPIKey = !(
    headers?.Authorization &&
    (headers?.Authorization.startsWith('Bearer pk_') ||
      headers?.Authorization.startsWith('Bearer ek_'))
  );

  const convertGetQueryToPostBodyForRequest =
    params['@rest'].method === 'GET' &&
    queryEndpointsToBeRequestedWithPost?.includes(params['@rest'].runtimePath);
  let path = runtimePathBuilder(
    operation.operationName,
    params['@rest'],
    mainDefinition,
    fragmentDefinitions,
    params.args?.input ?? {},
    isDashboardOnlyAPIKey,
    observabilityFns,
    convertGetQueryToPostBodyForRequest,
  );

  if (convertGetQueryToPostBodyForRequest) {
    params['@rest'].method = 'POST';
    const [basePath, queryString] = path.split('?');
    // Remove the query string from the path
    path = basePath;
    // ApolloLink builds the body from input. We fill it with the contents of the query string
    params.args.input = parseQueryParams(queryString);
  }

  if (params['@rest'].method === 'GET' && path.length >= MAX_INCLUDE_ONLY) {
    if (getEnvironment() === 'development') {
      console.warn(
        'V1 GraphQL request using GET method approaching maximum URL size',
        {
          endpoint: params['@rest'].runtimePath,
          project: queryProject,
          operationName: operation.operationName,
          size: path.length,
        },
      );
    }

    // Report to analytics when a GET request has a too long URL path.
    // If you're investigating such error for your query, please make the
    // query endpoint accept POST requests and add the endpoint to
    // frontend/data-v1/generator/post_as_query_paths.yaml.
    //
    // With this, the query will be executed using POST, and the limit that
    // some browsers have around long URLs will be bypassed.
    observabilityFns?.analytics.track('v1_graphql_request_with_long_url', {
      endpoint: params['@rest'].runtimePath,
      project: queryProject,
      operationName: operation.operationName,
      size: path.length,
    });
  }

  /**
   * Create a map where each path returned from `pathBuilder` is the key,
   * and the tokenized path minus the query string (e.g.
   * `/ajax/logs/:id`) is the value.
   *
   * We use a map because each operation may contain more than one REST
   * request.
   *
   * We use this later to send metrics with the tokenized path to keep
   * cardinality low.
   */
  operation.setContext(({paths}: RestLinkOperationContext) => {
    return {
      paths: {
        ...(paths || {}),
        [`/${path}`]: params['@rest']?.runtimePath,
      },
    };
  });

  if (shadowComparisonOperationsV1ToV2?.[operation.operationName]) {
    operation.setContext((prevCtx: RestLinkOperationContext) => ({
      ...prevCtx,
      underlyingRequest: params,
    }));
  }

  return path;
}
